import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material';
import { OpenInNew, Visibility, VisibilityOff } from '@mui/icons-material';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CLOUD_FUNCTION_URL_EUW2 } from '../../env';
import PrimaryButton from '../../components/shared/button/PrimaryButton';
import { MinorHeading, Subtitle } from '../../components/shared/typography/PrimaryTypography';
import PasswordValidationRuleList from '../../components/user_management/shared/PasswordValidationRuleList';
import { isValidPassword } from '../../lib/validation/text_validation';
import passwordImage from '../../assets/icons/Password.svg';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { log } from '../../util/log';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import useValidateToken from '../../hooks/useValidateToken';

function PasswordSetUpPage(): JSX.Element {
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [isBlurEvent, setIsBlurEvent] = useState<boolean>(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const theme = useTheme();
  const token = new URLSearchParams(window.location.hash.slice(1)).get('token');
  const navigate = useNavigate();
  const { isValid, isValidating, email } = useValidateToken(token);

  useEffect(() => {
    if (isValidating) return;
    if (!isValid) {
      navigate('/link-expired');
      return;
    }
    setIsLoading(false);
  }, [isValidating, isValid, navigate]);

  if (isValidating) return <LoadingScreen />;

  const handlePasswordSetClick = async () => {
    setIsLoading(true);
    try {
      if (!isValidPassword(password, '', '', '', confirmPassword)) {
        if (password !== confirmPassword) {
          setConfirmPasswordError('Passwords must match');
        }
        throw new Error('Password does not meet requirements.');
      }
      const response: Response = await fetch(`${CLOUD_FUNCTION_URL_EUW2}/updateUserPassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          metadata: { source: 'PasswordSetUpPage' },
          data: { params: { password } }
        })
      });
      if (!response.ok) {
        log('error', new Error('Failed to update password, please try again.'));
        throw new Error('Failed to update password, please try again.');
      }
      setFeedbackData({
        message: 'Password updated successfully! Redirecting to Syntiro.',
        state: true,
        type: 'success'
      });
      setTimeout(() => navigate('/'), 3000);
    } catch (err: unknown) {
      setIsLoading(false);
      const message =
        err instanceof TypeError
          ? 'Unable to reach the server. Please try again later.'
          : err instanceof Error
            ? err.message
            : 'An unknown error occurred.';
      setFeedbackData({ message, state: true, type: 'error' });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Card
        sx={{
          maxWidth: 480,
          width: '100%',
          p: 2,
          borderRadius: 4,
          boxShadow: 'none',
          border: 'none'
        }}
        elevation={0}
      >
        <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img src={passwordImage} width={180} style={{ marginBottom: 32 }} />
          <Subtitle weight="Bold" sx={{ mb: 6, fontSize: '2.2rem' }}>
            Create your password
          </Subtitle>
          <form>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  type={showPassword ? 'text' : 'password'}
                  label="Password"
                  fullWidth
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setIsBlurEvent(false);
                  }}
                  onBlur={() => setIsBlurEvent(true)}
                  error={isBlurEvent && !isValidPassword(password, '', '', '', confirmPassword)}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: 'grey.400' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirm password"
                  fullWidth
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (e.target.value === password) setConfirmPasswordError('');
                  }}
                  onBlur={() =>
                    setConfirmPasswordError(
                      password === confirmPassword ? '' : 'Passwords must match'
                    )
                  }
                  error={!!confirmPasswordError}
                  helperText={confirmPasswordError}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{ color: 'grey.400' }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid size={12}>
                <PasswordValidationRuleList
                  password={password}
                  first_name=""
                  last_name=""
                  username=""
                  is_blur_event={isBlurEvent}
                />
              </Grid>
              <Grid size={12} sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', pt: 2 }}>
                <PrimaryButton
                  onClick={handlePasswordSetClick}
                  loading={isLoading}
                  disabled={isLoading}
                  sx={{ px: 6 }}
                >
                  Create password
                </PrimaryButton>
              </Grid>
              <Grid size={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                <a href="/" style={{ textDecoration: 'none' }}>
                  <MinorHeading sx={{ color: theme.palette.teal.main, fontSize: '0.875rem' }}>
                    Back to landing page.
                  </MinorHeading>
                </a>
              </Grid>
              <Grid
                size={12}
                sx={{
                  pt: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 0.5,
                  alignItems: 'center'
                }}
              >
                <MinorHeading sx={{ fontSize: '0.75rem' }}>Having issues?</MinorHeading>
                <a
                  href="mailto:platformsupport@energypartners.co.za"
                  style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 2 }}
                >
                  <MinorHeading
                    weight="Medium"
                    sx={{ color: theme.palette.teal.main, fontSize: '0.75rem' }}
                  >
                    Contact Support
                  </MinorHeading>
                  <OpenInNew sx={{ fontSize: '0.75rem', color: theme.palette.teal.main }} />
                </a>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}

export default PasswordSetUpPage;
